import React, { useEffect, useMemo, useState } from "react";
import {
  FiCheckCircle,
  FiEdit2,
  FiFilter,
  FiFolder,
  FiLayers,
  FiLock,
  FiPlus,
  FiSave,
  FiSearch,
  FiShield,
  FiUsers,
} from "react-icons/fi";
import { MdDeleteOutline } from "react-icons/md";
import { FaBars } from "react-icons/fa";
import { List, arrayMove } from "react-movable";
import { Spinner } from "react-bootstrap";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import Select from "../../components/Select";
import Input from "../../components/input";
import CustomModal from "../../components/CustomModal";
import FileUpload from "../../components/FileUpload";
import DeleteModal from "../../components/modals/DeleteModal";
import { apiService } from "../../services/apiService";
import { errorNotify, successNotify } from "../../utils/toast";
import "../../styles/Access.css";
import "../../styles/Services.css";
import "../../styles/RoleManagement.css";

const ROLE_COLORS = [
  "#4014B8",
  "#1447B1",
  "#2B8A3E",
  "#D97706",
  "#DC2626",
  "#7C3AED",
  "#059669",
  "#0284C7",
];

const RoleManagement = () => {
  const [rolesList, setRolesList] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [activeRoleId, setActiveRoleId] = useState(null);
  const [modulesTree, setModulesTree] = useState([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState(new Set());
  const [initialPermissions, setInitialPermissions] = useState(new Set());
  const [saveLoading, setSaveLoading] = useState(false);

  // Modal states for Create/Edit
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("module"); // "module" | "submodule" | "permission"
  const [currentItem, setCurrentItem] = useState(null);
  const [currentMainModule, setCurrentMainModule] = useState(null);
  const [currentMenu, setCurrentMenu] = useState(null);
  const [modalName, setModalName] = useState("");
  const [modalNameError, setModalNameError] = useState("");
  const [iconFiles, setIconFiles] = useState([]);
  const [modalSaving, setModalSaving] = useState(false);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type, id, name }
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch Role Permissions Tree for a specific role
  const loadRolePermissions = async (roleId) => {
    if (!roleId) return;
    setActiveRoleId(roleId);
    setTreeLoading(true);

    try {
      const response = await apiService.getRolePermissionsTree(roleId);
      const rawTree = response?.data?.data || [];
      const sortedTree = [...rawTree]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((mod) => ({
          ...mod,
          menus: mod.menus
            ? [...mod.menus]
              .sort((a, b) => (a.order_num ?? 0) - (b.order_num ?? 0))
              .map((menu) => ({
                ...menu,
                permissions: menu.permissions
                  ? [...menu.permissions].sort(
                    (a, b) => (a.order_num ?? 0) - (b.order_num ?? 0)
                  )
                  : [],
              }))
            : [],
        }));
      setModulesTree(sortedTree);

      // Collect all selected permission IDs from API response
      const preSelectedIds = new Set();
      sortedTree.forEach((mod) => {
        (mod.menus || []).forEach((menu) => {
          (menu.permissions || []).forEach((perm) => {
            if (perm.selected_permission) {
              preSelectedIds.add(perm.id);
            }
          });
        });
      });

      setSelectedPermissions(preSelectedIds);
      setInitialPermissions(new Set(preSelectedIds));
    } catch (error) {
      console.error("Failed to fetch role permissions tree:", error);
      errorNotify(error || "Failed to load permissions for this role");
    } finally {
      setTreeLoading(false);
    }
  };

  // Initial fetch of Roles Dropdown
  useEffect(() => {
    const fetchRoles = async () => {
      setRolesLoading(true);
      try {
        const response = await apiService.getRolesDropdown();
        const rawRoles = response?.data || [];
        const formatted = rawRoles.map((role, idx) => ({
          id: role.id,
          name: role.name,
          description: role.description || `${role.name} role`,
          users: role.users_count ?? role.users ?? "-",
          status:
            role.status ||
            (String(role.name || "").toLowerCase().includes("super")
              ? "System"
              : "Active"),
          color: ROLE_COLORS[idx % ROLE_COLORS.length],
        }));

        setRolesList(formatted);

        if (formatted.length > 0) {
          const firstRoleId = formatted[0].id;
          loadRolePermissions(firstRoleId);
        }
      } catch (err) {
        console.error("Failed to fetch roles dropdown:", err);
        errorNotify("Failed to fetch roles list");
      } finally {
        setRolesLoading(false);
      }
    };

    fetchRoles();
  }, []);

  const activeRole =
    rolesList.find((role) => role.id === activeRoleId) ||
    rolesList[0] || {
      id: activeRoleId,
      name: "Role",
      description: "",
      users: 0,
      status: "Active",
      color: "#4014B8",
    };

  const roleSelectOptions = rolesList.map((role) => ({
    value: role.id,
    label: role.name,
  }));

  // All permission IDs in the current tree
  const allPermissionIds = useMemo(() => {
    const ids = [];
    modulesTree.forEach((mod) => {
      (mod.menus || []).forEach((menu) => {
        (menu.permissions || []).forEach((perm) => {
          ids.push(perm.id);
        });
      });
    });
    return ids;
  }, [modulesTree]);

  // Real-time search filtered modules & menus
  const filteredModules = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return modulesTree;

    return modulesTree
      .map((module) => {
        const moduleMatch = (module.name || "")
          .toLowerCase()
          .includes(normalizedQuery);
        const filteredMenus = (module.menus || []).filter((menu) => {
          const menuMatch = (menu.name || "")
            .toLowerCase()
            .includes(normalizedQuery);
          const permMatch = (menu.permissions || []).some((p) =>
            (p.name || "").toLowerCase().includes(normalizedQuery)
          );
          return moduleMatch || menuMatch || permMatch;
        });

        return {
          ...module,
          menus: moduleMatch ? module.menus : filteredMenus,
        };
      })
      .filter((module) => (module.menus || []).length > 0);
  }, [modulesTree, query]);

  const selectedCount = selectedPermissions.size;
  const coverage =
    allPermissionIds.length > 0
      ? Math.round((selectedCount / allPermissionIds.length) * 100)
      : 0;

  const togglePermission = (id) => {
    setSelectedPermissions((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const setPermissionGroup = (ids, shouldSelect) => {
    setSelectedPermissions((previous) => {
      const next = new Set(previous);
      ids.forEach((id) => {
        if (shouldSelect) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  };

  const getMenuPermIds = (menu) => (menu?.permissions || []).map((p) => p.id);

  const getModulePermIds = (module) =>
    (module?.menus || []).flatMap((m) => getMenuPermIds(m));

  const areAllSelected = (ids) =>
    ids && ids.length > 0 && ids.every((id) => selectedPermissions.has(id));

  const isDirty = useMemo(() => {
    if (selectedPermissions.size !== initialPermissions.size) return true;
    for (const id of selectedPermissions) {
      if (!initialPermissions.has(id)) return true;
    }
    return false;
  }, [selectedPermissions, initialPermissions]);

  // Save role permissions changes using /app-roles/assign-permissions
  const handleSaveRolePermissions = async () => {
    if (!activeRoleId || !isDirty) return;
    setSaveLoading(true);
    try {
      // Build permissions payload format: [{ module_id, modules_menu_id, permission_id }]
      const formattedPermissions = [];
      modulesTree.forEach((mod) => {
        (mod.menus || []).forEach((menu) => {
          (menu.permissions || []).forEach((perm) => {
            if (selectedPermissions.has(perm.id)) {
              formattedPermissions.push({
                module_id: mod.id,
                modules_menu_id: menu.id,
                permission_id: perm.id,
              });
            }
          });
        });
      });

      const payload = {
        role_id: Number(activeRoleId),
        permissions: formattedPermissions,
      };

      await apiService.assignRolePermissions(payload);
      successNotify("Permissions assigned successfully");
      setInitialPermissions(new Set(selectedPermissions));
      loadRolePermissions(activeRoleId);
    } catch (error) {
      console.error("Failed to assign role permissions:", error);
      errorNotify(error || "Failed to assign permissions");
    } finally {
      setSaveLoading(false);
    }
  };

  // Drag and drop reordering handlers
  const handleModuleReorder = async ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    const newModules = arrayMove(modulesTree, oldIndex, newIndex);
    setModulesTree(newModules);

    const payload = {
      module_orders: newModules.map((item, index) => ({
        id: item.id,
        order: index + 1,
      })),
    };

    try {
      await apiService.changeAppModuleOrder(payload);
      successNotify("Module order updated successfully");
    } catch (error) {
      console.error("Failed to update module order:", error);
      errorNotify(error || "Failed to update module order");
      loadRolePermissions(activeRoleId);
    }
  };

  // Modal Open Handlers
  const openCreateModuleModal = () => {
    setModalType("module");
    setCurrentItem(null);
    setCurrentMainModule(null);
    setCurrentMenu(null);
    setModalName("");
    setModalNameError("");
    setIconFiles([]);
    setShowModal(true);
  };

  const openEditModuleModal = (module) => {
    setModalType("module");
    setCurrentItem(module);
    setCurrentMainModule(module);
    setCurrentMenu(null);
    setModalName(module.name || "");
    setModalNameError("");
    if (module.icon) {
      const baseUrl = process.env.REACT_APP_API_FILES_URL || (process.env.REACT_APP_API_BASE_URL ? process.env.REACT_APP_API_BASE_URL.replace(/\/api\/?$/, '') : '');
      const fullUrl = module.icon.startsWith('http') ? module.icon : `${baseUrl}/${module.icon.replace(/^\/+/, '')}`;
      setIconFiles([{
        fileName: module.icon,
        originalName: module.icon,
        previewUrl: fullUrl,
        backend: true
      }]);
    } else {
      setIconFiles([]);
    }
    setShowModal(true);
  };

  const openCreateMenuModal = (module) => {
    setModalType("submodule");
    setCurrentItem(null);
    setCurrentMainModule(module);
    setCurrentMenu(null);
    setModalName("");
    setModalNameError("");
    setIconFiles([]);
    setShowModal(true);
  };

  const openEditMenuModal = (module, menu) => {
    setModalType("submodule");
    setCurrentItem(menu);
    setCurrentMainModule(module);
    setCurrentMenu(menu);
    setModalName(menu.name || "");
    setModalNameError("");
    if (menu.icon) {
      const baseUrl = process.env.REACT_APP_API_FILES_URL || (process.env.REACT_APP_API_BASE_URL ? process.env.REACT_APP_API_BASE_URL.replace(/\/api\/?$/, '') : '');
      const fullUrl = menu.icon.startsWith('http') ? menu.icon : `${baseUrl}/${menu.icon.replace(/^\/+/, '')}`;
      setIconFiles([{
        fileName: menu.icon,
        originalName: menu.icon,
        previewUrl: fullUrl,
        backend: true
      }]);
    } else {
      setIconFiles([]);
    }
    setShowModal(true);
  };

  const openCreatePermissionModal = (module, menu) => {
    setModalType("permission");
    setCurrentItem(null);
    setCurrentMainModule(module);
    setCurrentMenu(menu);
    setModalName("");
    setModalNameError("");
    setIconFiles([]);
    setShowModal(true);
  };

  const openEditPermissionModal = (module, menu, permission) => {
    setModalType("permission");
    setCurrentItem(permission);
    setCurrentMainModule(module);
    setCurrentMenu(menu);
    setModalName(permission.name || "");
    setModalNameError("");
    setIconFiles([]);
    setShowModal(true);
  };

  // Save handler for Module / Sub-module / Permission
  const handleModalSave = async (e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }

    if (!modalName.trim()) {
      setModalNameError(
        modalType === "permission"
          ? "Permission name is required"
          : modalType === "submodule"
            ? "Menu name is required"
            : "Module name is required"
      );
      return;
    }
    setModalNameError("");

    setModalSaving(true);
    try {
      const iconPath = iconFiles.length > 0 ? iconFiles[0].fileName : "";

      if (modalType === "permission") {
        if (currentItem) {
          await apiService.updatePermission({
            permission_id: currentItem.id,
            module_id: currentMainModule.id,
            modules_menu_id: currentMenu.id,
            name: modalName.trim(),
            order_num: currentItem.order_num ?? 0,
          });
          successNotify("Permission updated successfully");
        } else {
          await apiService.createPermission({
            module_id: currentMainModule.id,
            modules_menu_id: currentMenu.id,
            name: modalName.trim(),
          });
          successNotify("Permission created successfully");
        }
      } else if (modalType === "submodule") {
        if (currentItem) {
          await apiService.updateSubModule({
            sub_module_id: currentItem.id,
            module_id: currentMainModule?.id || currentItem.module_id,
            order_num: currentItem.order_num ?? currentItem.order ?? 0,
            name: modalName.trim(),
            icon: iconPath,
          });
          successNotify("Menu updated successfully");
        } else {
          await apiService.createSubModule({
            module_id: currentMainModule.id,
            name: modalName.trim(),
            icon: iconPath,
          });
          successNotify("Menu created successfully");
        }
      } else {
        // Module
        if (currentItem) {
          await apiService.updateAppModule({
            app_module_id: currentItem.id,
            name: modalName.trim(),
            icon: iconPath,
          });
          successNotify("Module updated successfully");
        } else {
          await apiService.createAppModule({
            name: modalName.trim(),
            icon: iconPath,
          });
          successNotify("Module created successfully");
        }
      }

      setShowModal(false);
      setModalName("");
      setIconFiles([]);
      setCurrentItem(null);
      setCurrentMainModule(null);
      setCurrentMenu(null);
      if (activeRoleId) {
        loadRolePermissions(activeRoleId);
      }
    } catch (error) {
      errorNotify(error || "Failed to save");
    } finally {
      setModalSaving(false);
    }
  };

  // Delete Handlers
  const confirmDelete = (type, item) => {
    setDeleteTarget({ type, id: item.id, name: item.name });
    setShowDeleteModal(true);
  };

  const handleDeleteExecute = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      if (deleteTarget.type === "module") {
        await apiService.deleteModule(deleteTarget.id);
        successNotify("Module deleted successfully");
      } else if (deleteTarget.type === "submodule") {
        await apiService.deleteSubModule(deleteTarget.id);
        successNotify("Menu deleted successfully");
      } else if (deleteTarget.type === "permission") {
        await apiService.deleteBusinessPermission(deleteTarget.id);
        successNotify("Permission deleted successfully");
      }
      setShowDeleteModal(false);
      setDeleteTarget(null);
      if (activeRoleId) {
        loadRolePermissions(activeRoleId);
      }
    } catch (error) {
      errorNotify(error || "Failed to delete");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="role_management_page">
      <Breadcrumbs values={["Access Management", "Role Management"]} />

      <div className="access_view">
        <h2>Role Management</h2>

        <div className="services_buttons">
          <button
            type="button"
            className="export_btn"
            onClick={openCreateModuleModal}
          >
            <FiPlus fontSize="16px" /> Create Module
          </button>
          <button
            type="button"
            disabled={!isDirty || saveLoading}
            onClick={handleSaveRolePermissions}
          >
            {saveLoading ? (
              <Spinner animation="border" size="sm" style={{ color: "#fff" }} />
            ) : (
              <FiSave fontSize="16px" />
            )}
            Save Changes
          </button>
        </div>
      </div>

      <div className="role_management_shell">
        <section className="role_management_metrics mt-3">
          <div className="role_metric">
            <span>
              <FiShield />
            </span>
            <div>
              <label>Permission Coverage</label>
              <strong>{coverage}%</strong>
            </div>
          </div>
          <div className="role_metric">
            <span>
              <FiCheckCircle />
            </span>
            <div>
              <label>Selected Permissions</label>
              <strong>{selectedCount}</strong>
            </div>
          </div>
          <div className="role_metric">
            <span>
              <FiUsers />
            </span>
            <div>
              <label>Assigned Users</label>
              <strong>{activeRole.users}</strong>
            </div>
          </div>
          <div className="role_metric">
            <span>
              <FiLock />
            </span>
            <div>
              <label>Role Status</label>
              <strong>{activeRole.status}</strong>
            </div>
          </div>
        </section>

        <div className="role_management_layout">
          <aside className="role_management_roles_panel">
            <div className="role_panel_header">
              <h5>Roles</h5>
              {rolesList.length > 0 && (
                <span className="role_count_badge">{rolesList.length}</span>
              )}
            </div>

            <div className="role_list">
              {rolesLoading && (
                <div
                  style={{
                    padding: "16px",
                    textAlign: "center",
                    color: "#808080",
                    fontSize: "13px",
                  }}
                >
                  <Spinner animation="border" size="sm" style={{ color: "#4014B8" }} />
                  <div style={{ marginTop: "6px" }}>Loading roles...</div>
                </div>
              )}
              {!rolesLoading && rolesList.length === 0 && (
                <div
                  style={{
                    padding: "16px",
                    textAlign: "center",
                    color: "#808080",
                    fontSize: "13px",
                  }}
                >
                  No roles found
                </div>
              )}
              {!rolesLoading &&
                rolesList.map((role) => (
                  <button
                    type="button"
                    key={role.id}
                    className={`role_list_item ${role.id === activeRoleId ? "active" : ""
                      }`}
                    onClick={() => loadRolePermissions(role.id)}
                  >
                    <span
                      className="role_color_dot"
                      style={{ backgroundColor: role.color }}
                    />
                    <div>
                      <strong>{role.name}</strong>
                      <small>{role.description}</small>
                    </div>
                    {role.users !== "-" && <em>{role.users}</em>}
                  </button>
                ))}
            </div>
          </aside>

          <main className="role_management_matrix">
            <div className="role_matrix_header">
              <div className="role_matrix_header_left">
                <h5>Permission Matrix</h5>
                <span className="role_active_badge">{activeRole.name}</span>
              </div>
              <Button
                iconButton
                shouldRow={false}
                type="button"
                icon={<FiCheckCircle />}
                iconBtnText={
                  areAllSelected(allPermissionIds) ? "Clear All" : "Select All"
                }
                onClick={() =>
                  setPermissionGroup(
                    allPermissionIds,
                    !areAllSelected(allPermissionIds)
                  )
                }
              />
            </div>

            <div className="role_matrix_table_wrap">
              {treeLoading && (
                <div
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#808080",
                    fontSize: "14px",
                  }}
                >
                  <Spinner
                    animation="border"
                    size="sm"
                    style={{ color: "#4014B8", width: "24px", height: "24px" }}
                  />
                  <div style={{ marginTop: "10px", fontWeight: "500" }}>
                    Loading permissions tree for {activeRole.name}...
                  </div>
                </div>
              )}

              {!treeLoading && filteredModules.length === 0 && (
                <div
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#808080",
                    fontSize: "14px",
                  }}
                >
                  No permissions match your search query.
                </div>
              )}

              {!treeLoading &&
                (!query.trim() ? (
                  <List
                    values={modulesTree}
                    onChange={handleModuleReorder}
                    lockVertically={true}
                    renderList={({ children, props: listProps }) => (
                      <div {...listProps} style={{ width: "100%" }}>
                        {children}
                      </div>
                    )}
                    renderItem={({
                      value: module,
                      props: moduleProps,
                      isDragged: isModuleDragged,
                    }) => {
                      const {
                        onMouseDown: mOnMouseDown,
                        onTouchStart: mOnTouchStart,
                        ...otherModuleProps
                      } = moduleProps;
                      const modulePermIds = getModulePermIds(module);
                      const moduleChecked = areAllSelected(modulePermIds);

                      return (
                        <div
                          {...otherModuleProps}
                          style={{
                            ...otherModuleProps.style,
                            marginBottom: "12px",
                            opacity: isModuleDragged ? 0.9 : 1,
                            boxShadow: isModuleDragged
                              ? "0 10px 25px rgba(0,0,0,0.15)"
                              : undefined,
                            zIndex: isModuleDragged ? 9999 : undefined,
                            width: "100%",
                          }}
                          className="role_module_section"
                          key={module.id}
                        >
                          <div className="role_module_bar">
                            <div className="role_module_title_area">
                              <span
                                className="role_drag_grip"
                                title="Drag to reorder module"
                                data-movable-handle
                                onMouseDown={mOnMouseDown}
                                onTouchStart={mOnTouchStart}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <FaBars size={13} />
                              </span>
                              <span className="role_module_icon_badge">
                                {module.icon ? (
                                  <img
                                    src={
                                      module.icon.startsWith("http")
                                        ? module.icon
                                        : `${process.env
                                          .REACT_APP_API_FILES_URL ||
                                        (process.env.REACT_APP_API_BASE_URL
                                          ? process.env.REACT_APP_API_BASE_URL.replace(
                                            /\/api\/?$/,
                                            ""
                                          )
                                          : "")
                                        }/${module.icon.replace(/^\/+/, "")}`
                                    }
                                    alt=""
                                    style={{
                                      width: "18px",
                                      height: "18px",
                                      objectFit: "contain",
                                    }}
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                      const fallback =
                                        e.currentTarget.nextSibling;
                                      if (fallback)
                                        fallback.style.display = "inline-flex";
                                    }}
                                  />
                                ) : null}
                                <span
                                  style={{
                                    display: module.icon
                                      ? "none"
                                      : "inline-flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <FiLayers />
                                </span>
                              </span>
                              <strong>{module.name}</strong>
                              <span className="role_module_perm_count">
                                {modulePermIds.length} permissions
                              </span>
                            </div>

                            <div className="role_module_actions">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={moduleChecked}
                                  onChange={() =>
                                    setPermissionGroup(
                                      modulePermIds,
                                      !moduleChecked
                                    )
                                  }
                                />
                                Module access
                              </label>

                              <button
                                type="button"
                                className="role_add_btn_sm"
                                title="Add Menu / Sub-module"
                                onClick={() => openCreateMenuModal(module)}
                              >
                                <FiPlus /> Add Menu
                              </button>

                              <button
                                type="button"
                                className="role_action_btn"
                                title="Edit Module"
                                onClick={() => openEditModuleModal(module)}
                              >
                                <FiEdit2 />
                              </button>

                              <button
                                type="button"
                                className="role_action_btn delete"
                                title="Delete Module"
                                onClick={() => confirmDelete("module", module)}
                              >
                                <MdDeleteOutline />
                              </button>
                            </div>
                          </div>

                          <div className="role_permissions_grid">
                            {(module.menus || []).map((menu) => {
                              const menuPermIds = getMenuPermIds(menu);
                              const menuChecked = areAllSelected(menuPermIds);

                              return (
                                <div
                                  className="role_permission_row"
                                  key={menu.id}
                                >
                                  <div className="role_menu_cell">
                                    <div className="role_menu_header_row">
                                      <strong>{menu.name}</strong>
                                      <div className="role_menu_actions">
                                        <button
                                          type="button"
                                          className="role_action_btn"
                                          title="Add Permission"
                                          onClick={() =>
                                            openCreatePermissionModal(
                                              module,
                                              menu
                                            )
                                          }
                                        >
                                          <FiPlus />
                                        </button>
                                        <button
                                          type="button"
                                          className="role_action_btn"
                                          title="Edit Menu"
                                          onClick={() =>
                                            openEditMenuModal(module, menu)
                                          }
                                        >
                                          <FiEdit2 />
                                        </button>
                                        <button
                                          type="button"
                                          className="role_action_btn delete"
                                          title="Delete Menu"
                                          onClick={() =>
                                            confirmDelete("submodule", menu)
                                          }
                                        >
                                          <MdDeleteOutline />
                                        </button>
                                      </div>
                                    </div>

                                    <label>
                                      <input
                                        type="checkbox"
                                        checked={menuChecked}
                                        onChange={() =>
                                          setPermissionGroup(
                                            menuPermIds,
                                            !menuChecked
                                          )
                                        }
                                      />
                                      Row access
                                    </label>
                                  </div>

                                  <div className="role_permission_cell">
                                    {(menu.permissions || []).map(
                                      (permission) => {
                                        const checked =
                                          selectedPermissions.has(
                                            permission.id
                                          );

                                        return (
                                          <div
                                            key={permission.id}
                                            className={`permission_pill ${checked ? "selected" : ""
                                              }`}
                                            onClick={() =>
                                              togglePermission(permission.id)
                                            }
                                            role="button"
                                            tabIndex={0}
                                          >
                                            <span
                                              className="permission_pill_checkbox"
                                              aria-hidden="true"
                                            />
                                            <span className="permission_pill_text">
                                              {permission.name}
                                            </span>
                                            <div className="permission_pill_actions">
                                              <button
                                                type="button"
                                                className="permission_pill_action_btn"
                                                title="Edit Permission"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  openEditPermissionModal(
                                                    module,
                                                    menu,
                                                    permission
                                                  );
                                                }}
                                              >
                                                <FiEdit2 />
                                              </button>
                                              <button
                                                type="button"
                                                className="permission_pill_action_btn delete"
                                                title="Delete Permission"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  confirmDelete(
                                                    "permission",
                                                    permission
                                                  );
                                                }}
                                              >
                                                <MdDeleteOutline />
                                              </button>
                                            </div>
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }}
                  />
                ) : (
                  filteredModules.map((module) => {
                    const modulePermIds = getModulePermIds(module);
                    const moduleChecked = areAllSelected(modulePermIds);

                    return (
                      <div className="role_module_section" key={module.id}>
                        <div className="role_module_bar">
                          <div className="role_module_title_area">
                            <span className="role_module_icon_badge">
                              {module.icon ? (
                                <img
                                  src={
                                    module.icon.startsWith("http")
                                      ? module.icon
                                      : `${process.env.REACT_APP_API_FILES_URL ||
                                      (process.env.REACT_APP_API_BASE_URL
                                        ? process.env.REACT_APP_API_BASE_URL.replace(
                                          /\/api\/?$/,
                                          ""
                                        )
                                        : "")
                                      }/${module.icon.replace(/^\/+/, "")}`
                                  }
                                  alt=""
                                  style={{
                                    width: "18px",
                                    height: "18px",
                                    objectFit: "contain",
                                  }}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                    const fallback =
                                      e.currentTarget.nextSibling;
                                    if (fallback)
                                      fallback.style.display = "inline-flex";
                                  }}
                                />
                              ) : null}
                              <span
                                style={{
                                  display: module.icon ? "none" : "inline-flex",
                                  alignItems: "center",
                                }}
                              >
                                <FiLayers />
                              </span>
                            </span>
                            <strong>{module.name}</strong>
                            <span className="role_module_perm_count">
                              {modulePermIds.length} permissions
                            </span>
                          </div>

                          <div className="role_module_actions">
                            <label>
                              <input
                                type="checkbox"
                                checked={moduleChecked}
                                onChange={() =>
                                  setPermissionGroup(
                                    modulePermIds,
                                    !moduleChecked
                                  )
                                }
                              />
                              Module access
                            </label>

                            <button
                              type="button"
                              className="role_add_btn_sm"
                              title="Add Menu / Sub-module"
                              onClick={() => openCreateMenuModal(module)}
                            >
                              <FiPlus /> Add Menu
                            </button>

                            <button
                              type="button"
                              className="role_action_btn"
                              title="Edit Module"
                              onClick={() => openEditModuleModal(module)}
                            >
                              <FiEdit2 />
                            </button>

                            <button
                              type="button"
                              className="role_action_btn delete"
                              title="Delete Module"
                              onClick={() => confirmDelete("module", module)}
                            >
                              <MdDeleteOutline />
                            </button>
                          </div>
                        </div>

                        <div className="role_permissions_grid">
                          {(module.menus || []).map((menu) => {
                            const menuPermIds = getMenuPermIds(menu);
                            const menuChecked = areAllSelected(menuPermIds);

                            return (
                              <div
                                className="role_permission_row"
                                key={menu.id}
                              >
                                <div className="role_menu_cell">
                                  <div className="role_menu_header_row">
                                    <strong>{menu.name}</strong>
                                    <div className="role_menu_actions">
                                      <button
                                        type="button"
                                        className="role_action_btn"
                                        title="Add Permission"
                                        onClick={() =>
                                          openCreatePermissionModal(
                                            module,
                                            menu
                                          )
                                        }
                                      >
                                        <FiPlus />
                                      </button>
                                      <button
                                        type="button"
                                        className="role_action_btn"
                                        title="Edit Menu"
                                        onClick={() =>
                                          openEditMenuModal(module, menu)
                                        }
                                      >
                                        <FiEdit2 />
                                      </button>
                                      <button
                                        type="button"
                                        className="role_action_btn delete"
                                        title="Delete Menu"
                                        onClick={() =>
                                          confirmDelete("submodule", menu)
                                        }
                                      >
                                        <MdDeleteOutline />
                                      </button>
                                    </div>
                                  </div>

                                  <label>
                                    <input
                                      type="checkbox"
                                      checked={menuChecked}
                                      onChange={() =>
                                        setPermissionGroup(
                                          menuPermIds,
                                          !menuChecked
                                        )
                                      }
                                    />
                                    Row access
                                  </label>
                                </div>

                                <div className="role_permission_cell">
                                  {(menu.permissions || []).map(
                                    (permission) => {
                                      const checked =
                                        selectedPermissions.has(
                                          permission.id
                                        );

                                      return (
                                        <div
                                          key={permission.id}
                                          className={`permission_pill ${checked ? "selected" : ""
                                            }`}
                                          onClick={() =>
                                            togglePermission(permission.id)
                                          }
                                          role="button"
                                          tabIndex={0}
                                        >
                                          <span
                                            className="permission_pill_checkbox"
                                            aria-hidden="true"
                                          />
                                          <span className="permission_pill_text">
                                            {permission.name}
                                          </span>
                                          <div className="permission_pill_actions">
                                            <button
                                              type="button"
                                              className="permission_pill_action_btn"
                                              title="Edit Permission"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openEditPermissionModal(
                                                  module,
                                                  menu,
                                                  permission
                                                );
                                              }}
                                            >
                                              <FiEdit2 />
                                            </button>
                                            <button
                                              type="button"
                                              className="permission_pill_action_btn delete"
                                              title="Delete Permission"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                confirmDelete(
                                                  "permission",
                                                  permission
                                                );
                                              }}
                                            >
                                              <MdDeleteOutline />
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                ))}
            </div>
          </main>
        </div>
      </div>

      {/* Modal for Create / Edit Module, Submodule, Permission */}
      <CustomModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        size="md"
        heading={
          modalType === "permission"
            ? currentItem
              ? "Edit Permission"
              : `Create Permission - ${currentMenu?.name || ""}`
            : modalType === "submodule"
              ? currentItem
                ? "Edit Menu"
                : `Create Menu - ${currentMainModule?.name || ""}`
              : currentItem
                ? "Edit Module"
                : "Create Module"
        }
      >
        <div className="role_form_modal_content">
          <div className="mb-3">
            <Input
              label="Name"
              name="modalName"
              type="text"
              placeholder={
                modalType === "permission"
                  ? "Enter permission name (e.g. Create Vehicle)"
                  : modalType === "submodule"
                    ? "Enter menu name (e.g. Vehicles)"
                    : "Enter module name (e.g. Assets Management)"
              }
              value={modalName}
              onChange={(e) => {
                setModalName(e.target.value);
                if (e.target.value.trim()) {
                  setModalNameError("");
                }
              }}
              error={modalNameError}
              touched={Boolean(modalNameError)}
              required={true}
            />
          </div>

          {modalType !== "permission" && (
            <div className="mb-3">
              <label className="role_modal_field_label">Icon (Optional)</label>
              <FileUpload
                files={iconFiles}
                setFiles={setIconFiles}
                destination="app-modules"
                maxFileSize={5}
              />
            </div>
          )}

          <div className="role_modal_actions">
            <Button
              type="button"
              cancelBtn={() => setShowModal(false)}
              submitBtn={handleModalSave}
              loading={modalSaving}
              disabled={modalSaving}
              submitText={currentItem ? "Save Changes" : "Create"}
              cancelBtnText="Cancel"
              isShowCancel={true}
              isShowSubmitBtn={true}
              shouldRow={true}
            />
          </div>
        </div>
      </CustomModal>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteTarget(null);
        }}
        onLogout={handleDeleteExecute}
        loading={deleteLoading}
        topHeading={`Delete ${deleteTarget?.type === "module"
          ? "Module"
          : deleteTarget?.type === "submodule"
            ? "Menu"
            : "Permission"
          }`}
        description={`Are you sure you want to delete ${deleteTarget?.type === "module"
          ? "module"
          : deleteTarget?.type === "submodule"
            ? "menu"
            : "permission"
          } "${deleteTarget?.name}"?`}
        subDescription={
          deleteTarget?.type === "permission"
            ? "This permission will be permanently removed from all roles."
            : "This action cannot be undone and will permanently remove associated permissions."
        }
      />
    </div>
  );
};

export default RoleManagement;
